import React, { useState, useEffect } from 'react';
import { customPropertyListValueService } from '../../services';
import {
  Checkbox,
  Input,
  Label,
  SelectOwner,
  Select,
  formatSelectOptions,
  TextareaCode,
  Textarea,
  textareaInitialState,
  textareaCodeInitialState,
  editorStateToPlainText,
} from '../../components';
import { useMessage } from '../../hooks';

export const CustomPropertiesForm = ({
  customProperties,
  formData,
  onChange,
  batchEdit,
  batchEditItems,
  loading,
}) => {
  const [loadingCustomProperties, setLoadingCustomProperties] = useState(false);
  const [listOptions, setListOptions] = useState({});

  const { showMessage } = useMessage();

  const handleChange = (event, customPropertyTypeCode, customPropertyId, formPart) => {
    event['customPropertyTypeCode'] = customPropertyTypeCode;
    event['customPropertyId'] = customPropertyId;

    onChange && onChange(event, formPart);
  };

  useEffect(() => {
    const lists = customProperties
      .filter(x => x.custom_property_type_code === 'list')
      .map(x => x.custom_property_id);

    if (lists.length > 0) {
      fetchCustomPropertyListValues(lists);
    }
  }, [customProperties]);

  const fetchCustomPropertyListValues = async ids => {
    const idsString = ids.join(',');
    customPropertyListValueService.getByCustomPropertyId(idsString).then(res => {
      if (res) {
        const customPropertyListValues = res.custom_property_list_values;
        const tmpListOptions = {};

        customPropertyListValues.forEach(x => {
          tmpListOptions[x.custom_property_id] = [
            ...(tmpListOptions[x.custom_property_id] || []),
            x,
          ];
        });

        setListOptions(tmpListOptions);
      }
    });
    return;
  };

  return (
    <React.Fragment>
      {customProperties.length > 0 &&
        customProperties.map(item => {
          let rawValue;
          let element;

          switch (item.custom_property_type_code) {
            case 'boolean':
              element = (
                <div className="horizontal">
                  <Checkbox
                    name={item.custom_property_code}
                    checked={formData[item.custom_property_code]?.value || false}
                    label={item.custom_property_name}
                    title={item.custom_property_description}
                    onChange={e =>
                      handleChange(e, item.custom_property_type_code, item.custom_property_id)
                    }
                    disabled={
                      loading ||
                      loadingCustomProperties ||
                      (batchEdit && !batchEditItems['batch_' + item.custom_property_code])
                    }
                  />
                </div>
              );
              break;
            case 'code':
              rawValue =
                formData[item.custom_property_code]?.value &&
                editorStateToPlainText(formData[item.custom_property_code]?.value);
              element = (
                <TextareaCode
                  name={item.custom_property_code}
                  value={
                    rawValue === 'multiple'
                      ? textareaCodeInitialState({ value: '' })
                      : formData[item.custom_property_code]?.value ||
                        textareaCodeInitialState({ value: '' })
                  }
                  label={!batchEdit && item.custom_property_name}
                  title={item.custom_property_description}
                  showLineNumbers={false}
                  onChange={e =>
                    handleChange(e, item.custom_property_type_code, item.custom_property_id)
                  }
                  placeholder={rawValue === 'multiple' ? '< Multiple >' : ''}
                  disabled={
                    loading ||
                    loadingCustomProperties ||
                    (batchEdit && !batchEditItems['batch_' + item.custom_property_code])
                  }
                  isOptional={true}
                  expandable={true}
                />
              );
              break;
            case 'description':
              rawValue =
                formData[item.custom_property_code]?.value &&
                editorStateToPlainText(formData[item.custom_property_code]?.value);
              element = (
                <Textarea
                  name={item.custom_property_code}
                  value={
                    rawValue === 'multiple'
                      ? textareaInitialState({ value: '' })
                      : formData[item.custom_property_code]?.value ||
                        textareaInitialState({ value: '' })
                  }
                  label={!batchEdit && item.custom_property_name}
                  title={item.custom_property_description}
                  onChange={e =>
                    handleChange(e, item.custom_property_type_code, item.custom_property_id)
                  }
                  enableReferences={false}
                  placeholder={rawValue === 'multiple' ? '< Multiple >' : ''}
                  disabled={
                    loading ||
                    loadingCustomProperties ||
                    (batchEdit && !batchEditItems['batch_' + item.custom_property_code])
                  }
                  isOptional={true}
                />
              );
              break;
            case 'string':
              element = (
                <Input
                  type="text"
                  name={item.custom_property_code}
                  value={
                    formData[item.custom_property_code]?.value === 'multiple'
                      ? ''
                      : formData[item.custom_property_code]?.value || ''
                  }
                  onChange={e =>
                    handleChange(e, item.custom_property_type_code, item.custom_property_id)
                  }
                  label={!batchEdit && item.custom_property_name}
                  title={item.custom_property_description}
                  placeholder={
                    formData[item.custom_property_code]?.value === 'multiple' ? '< Multiple >' : ''
                  }
                  disabled={
                    loading ||
                    loadingCustomProperties ||
                    (batchEdit && !batchEditItems['batch_' + item.custom_property_code])
                  }
                  isOptional={true}
                />
              );
              break;
            case 'datetime':
              element = (
                <Input
                  type="text"
                  name={item.custom_property_code}
                  value={
                    formData[item.custom_property_code]?.value === 'multiple'
                      ? ''
                      : formData[item.custom_property_code]?.value || ''
                  }
                  onChange={e =>
                    handleChange(e, item.custom_property_type_code, item.custom_property_id)
                  }
                  label={!batchEdit && item.custom_property_name}
                  title={item.custom_property_description}
                  placeholder={
                    formData[item.custom_property_code]?.value === 'multiple'
                      ? '< Multiple >'
                      : 'Enter a date...'
                  }
                  disabled={
                    loading ||
                    loadingCustomProperties ||
                    (batchEdit && !batchEditItems['batch_' + item.custom_property_code])
                  }
                  isOptional={true}
                />
              );
              break;
            case 'list':
              element = (
                <Select
                  name={item.custom_property_code}
                  value={
                    formData[item.custom_property_code]?.value === 'multiple'
                      ? undefined
                      : parseInt(formData[item.custom_property_code]?.value) || ''
                  }
                  label={!batchEdit && item.custom_property_name}
                  options={formatSelectOptions({
                    options: listOptions[item.custom_property_id] || [],
                    optionValue: 'custom_property_list_value_id',
                    optionLabel: 'custom_property_list_value_name',
                    tooltip: 'custom_property_list_value_description',
                    optionSecondaryLabel: 'custom_property_list_value_code',
                  })}
                  title={item.custom_property_description}
                  isNullable={true}
                  placeholder={
                    formData[item.custom_property_code]?.value === 'multiple'
                      ? '< Multiple >'
                      : `Select ${item.custom_property_name}...`
                  }
                  onChange={e =>
                    handleChange(e, item.custom_property_type_code, item.custom_property_id)
                  }
                  disabled={
                    loading ||
                    loadingCustomProperties ||
                    (batchEdit && !batchEditItems['batch_' + item.custom_property_code])
                  }
                  isOptional={true}
                />
              );
              break;
            case 'url':
              element = (
                <React.Fragment>
                  {!batchEdit && (
                    <Label
                      label={item.custom_property_name}
                      title={item.custom_property_description}
                      disabled={loading || loadingCustomProperties}
                    />
                  )}
                  <Input
                    type="text"
                    name={item.custom_property_code}
                    value={
                      formData[item.custom_property_code]?.value === 'multiple'
                        ? ''
                        : formData[item.custom_property_code]?.value || ''
                    }
                    onChange={e =>
                      handleChange(e, item.custom_property_type_code, item.custom_property_id)
                    }
                    label="URL"
                    title="Link URL"
                    placeholder={
                      formData[item.custom_property_code]?.value === 'multiple'
                        ? '< Multiple >'
                        : 'http://'
                    }
                    disabled={
                      loading ||
                      loadingCustomProperties ||
                      (batchEdit && !batchEditItems['batch_' + item.custom_property_code])
                    }
                    labelPosition="left"
                    isOptional={true}
                  />
                  <Input
                    type="text"
                    name={item.custom_property_code + '__secondary_value'}
                    value={
                      formData[item.custom_property_code + '__secondary_value']?.value ===
                      'multiple'
                        ? ''
                        : formData[item.custom_property_code + '__secondary_value']?.value || ''
                    }
                    onChange={e =>
                      handleChange(e, item.custom_property_type_code, item.custom_property_id)
                    }
                    label="Display Name"
                    title="Optional display name for the URL"
                    placeholder={
                      formData[item.custom_property_code + '__secondary_value']?.value ===
                      'multiple'
                        ? '< Multiple >'
                        : ''
                    }
                    disabled={
                      loading ||
                      loadingCustomProperties ||
                      (batchEdit && !batchEditItems['batch_' + item.custom_property_code])
                    }
                    labelPosition="left"
                    isOptional={true}
                  />
                </React.Fragment>
              );
              break;
            case 'user':
              element = (
                <SelectOwner
                  name={item.custom_property_code}
                  value={
                    formData[item.custom_property_code]?.value === 'multiple'
                      ? undefined
                      : parseInt(formData[item.custom_property_code]?.value) || ''
                  }
                  label={!batchEdit && item.custom_property_name}
                  title={item.custom_property_description}
                  isNullable={true}
                  placeholder={
                    formData[item.custom_property_code]?.value === 'multiple' ? '< Multiple >' : ''
                  }
                  onChange={e =>
                    handleChange(e, item.custom_property_type_code, item.custom_property_id)
                  }
                  disabled={
                    loading ||
                    loadingCustomProperties ||
                    (batchEdit && !batchEditItems['batch_' + item.custom_property_code])
                  }
                  isOptional={true}
                />
              );
              break;
            default:
            // nothing
          }

          return (
            <div className="form-block vertical" key={item.custom_property_code}>
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems['batch_' + item.custom_property_code]}
                    name={'batch_' + item.custom_property_code}
                    label={'Edit ' + item.custom_property_name}
                    onChange={e =>
                      handleChange(
                        e,
                        item.custom_property_type_code,
                        item.custom_property_id,
                        'batch'
                      )
                    }
                    disabled={loading}
                  />
                </label>
              )}
              {element}
            </div>
          );
        })}
    </React.Fragment>
  );
};
